import { useState } from 'react';
import { Button, Icon, Input, Sheet } from '@astral/ui';
import { CUSTOM_CATEGORY_NAME_MAX, type CustomCategoryDTO } from '@astral/shared';
import { api, ApiClientError } from '../lib/api.js';
import { useT, type TranslationKey } from '../providers/I18nProvider.js';
import { useToast } from '../providers/ToastProvider.js';
import { ConfirmSheet } from './ConfirmSheet.js';

export interface CategoryManagerSheetProps {
  open: boolean;
  categories: CustomCategoryDTO[];
  onClose: () => void;
  /** Called after a successful create or delete so the caller can refresh. */
  onChanged: (event: { deletedId?: string }) => void;
}

/** Server validation codes → localized copy. */
const ERROR_KEYS: Record<string, TranslationKey> = {
  EMPTY: 'categoryManager.errorRequired',
  TOO_LONG: 'categoryManager.errorTooLong',
  DUPLICATE: 'categoryManager.errorDuplicate',
  RESERVED: 'categoryManager.errorReserved',
};

/**
 * Create and delete the user's own project categories.
 *
 * One sheet for both, because the list is short and splitting "add" from
 * "manage" across two surfaces would be more chrome than content. Built on the
 * existing `Sheet`, `Input` and `ConfirmSheet` — no new patterns.
 *
 * Validation is the SERVER'S: it owns the duplicate and reserved-name rules
 * (including collisions with built-in labels in any language), so the client
 * renders the code it returns rather than re-deriving the rule and drifting.
 */
export function CategoryManagerSheet({
  open,
  categories,
  onClose,
  onChanged,
}: CategoryManagerSheetProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [name, setName] = useState('');
  const [error, setError] = useState<TranslationKey | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<CustomCategoryDTO | null>(null);

  const create = async (): Promise<void> => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError('categoryManager.errorRequired');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createCustomCategory({ name: trimmed });
      setName('');
      toast.show(t('categoryManager.created'), 'success');
      onChanged({});
    } catch (err) {
      const code = err instanceof ApiClientError ? err.details?.name?.[0] : undefined;
      setError(ERROR_KEYS[code ?? ''] ?? 'categoryManager.errorFailed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (target: CustomCategoryDTO): Promise<void> => {
    setBusy(true);
    try {
      await api.deleteCustomCategory(target.id);
      toast.show(t('categoryManager.deleted'), 'default');
      // The id travels back so the caller can drop a filter that just vanished.
      onChanged({ deletedId: target.id });
    } catch {
      toast.show(t('categoryManager.errorFailed'), 'error');
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  return (
    <>
      <Sheet open={open} onClose={onClose} title={t('categoryManager.manage')}>
        <div className="stack" style={{ gap: 16 }}>
          <div className="stack" style={{ gap: 8 }}>
            <Input
              label={t('categoryManager.name')}
              value={name}
              maxLength={CUSTOM_CATEGORY_NAME_MAX}
              onChange={(event) => {
                setName(event.target.value);
                if (error) setError(null);
              }}
            />
            {error && (
              <span className="packlink-bad" style={{ fontSize: 12 }}>
                {t(error)}
              </span>
            )}
            <Button
              fullWidth
              loading={busy}
              disabled={busy || name.trim().length === 0}
              leftIcon={<Icon name="check" size={18} />}
              onClick={() => void create()}
            >
              {t('categoryManager.create')}
            </Button>
          </div>

          {categories.length === 0 ? (
            <p className="muted" style={{ fontSize: 13, margin: 0 }}>
              {t('categoryManager.empty')}
            </p>
          ) : (
            <div className="stack" style={{ gap: 6 }}>
              {categories.map((entry) => (
                <div key={entry.id} className="row-between">
                  <span>{entry.name}</span>
                  <button
                    className="icon-btn icon-btn--sm"
                    aria-label={`${t('categoryManager.delete')} ${entry.name}`}
                    disabled={busy}
                    onClick={() => setConfirming(entry)}
                  >
                    <Icon name="trash" size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Sheet>

      {/*
        Deleting is destructive to the CATEGORY only, so the confirmation says
        plainly what happens to the projects inside it — they survive and become
        uncategorised.
      */}
      <ConfirmSheet
        open={confirming !== null}
        title={t('categoryManager.deleteConfirm')}
        message={t('categoryManager.deleteBody')}
        confirmLabel={t('common.delete')}
        danger
        loading={busy}
        onConfirm={() => confirming && void remove(confirming)}
        onCancel={() => setConfirming(null)}
      />
    </>
  );
}
